<template>
  <div class="modal modal-filter-details fade" id="replace-talent" role="dialog" aria-labelledby="location-label" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title" id="location-label">Replace Booked Talent</h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">

            <div :class="$hasErrorClass($v.talent, 'form-group')">
              <label class="control-label col-sm-4">Replace</label>
              <div class="col-sm-8">
                <selectize
                  name="attending_talent"
                  class="selectize selectize-single form-control"
                  v-model="$v.talent.$model"
                  :options="attendingTalentOptions"/>

                <vuelidate-help label="Attending talent" :field="$v.talent"/>
              </div>
            </div>

            <div :class="$hasErrorClass($v.replacement, 'form-group')">
              <label class="control-label col-sm-4">With</label>
              <div class="col-sm-8">
                <selectize
                  name="replacement_talent"
                  class="selectize selectize-single form-control"
                  v-model="$v.replacement.$model"
                  :options="initialReplacementTalentOptions"
                  :score="replacementScoringCallback"
                  :fetch-results="handleFetchReplacementTalentCallback"/>

                <vuelidate-help label="Replacement talent" :field="$v.replacement"/>
              </div>
            </div>

          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-lg btn-danger pull-left" data-dismiss="modal"><i class="fa fa-close"></i> Cancel</button>
          <ladda-button
            type="button"
            class="btn btn-lg btn-primary"
            :click="handleSaveReplacedTalentCallback">
            <i class="fa fa-check"></i> Save
          </ladda-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { helpers } from 'mixins/bookings'
import { map, filter, indexOf, concat, merge } from 'lodash'
import $ from 'jquery'
import Selectize from '../ext/codium/CodiumSelectize'
import VuelidateHelp from '../misc/VuelidateHelp'
import { notify } from 'utils'
import { required } from 'vuelidate/lib/validators'

export default {
  mixins: [helpers],

  validations: {
    talent: {
      required
    },
    replacement: {
      required
    }
  },

  props: ['getTalent'],

  data () {
    return {
      initialReplacementTalent: [],
      talent: null,
      replacement: null
    }
  },

  computed: {
    ...mapState('booking', ['booking']),

    /**
     * Attending talent as Selectize options.
     *
     * @return array
     */
    attendingTalentOptions () {
      return map(this.bookingAttendingTalent, this.mapTalentForSelectizeOption)
    },

    /**
     * Attenting talent as just IDs for comparison.
     *
     * @return array
     */
    attendingTalentIds () {
      return map(this.bookingAttendingTalent, talent => talent.id)
    },

    /**
     * Initial replacement talent Selectize options.
     *
     * @return array
     */
    initialReplacementTalentOptions () {
      // Only add the option to remove a talent if we have more than 1 talent slot available
      // on the booking, otherwise they can only replace.
      if (this.bookingAttendingTalent.length > 1) {
        return this.filterOutTalentThatAreAttending(
          concat([{ id: 'remove', text: 'Remove Additional Talent' }], this.initialReplacementTalent)
        )
      }

      return this.filterOutTalentThatAreAttending(this.initialReplacementTalent)
    }
  },

  watch: {
    getTalent: function (val) {
      if (val) {
        this.getTalentList()
          .then((response) => {
            this.initialReplacementTalent = map(response.data.data, this.mapTalentForSelectizeOption)
          })
      }
    }
  },

  components: {
    Selectize,
    VuelidateHelp
  },

  methods: {
    ...mapActions('booking', ['replaceTalent']),

    /**
     * Get talent via the talent list controller as it is better for Selectize.
     *
     * @return Promise
     */
    getTalentList (params = {}) {
      return this.$http.get('talent-list', { params: merge(params, { visibility: 'all' }) })
    },

    /**
     * Handle fetching the repacement talent for Selectize.
     *
     * @return Promise
     */
    handleFetchReplacementTalentCallback (name, callback) {
      return this.getTalentList({ name })
        .then((response) => {
          callback(
            this.filterOutTalentThatAreAttending(map(response.data.data, this.mapTalentForSelectizeOption))
          )
        })
    },

    /**
     * Handle saving the replaced talent.
     *
     * @return Promise
     */
    handleSaveReplacedTalentCallback () {
      if (this.$v.$invalid) {
        this.$v.$touch()
        notify.error(`Some fields failed validation. Please fix them and try again`)
        return Promise.resolve()
      }
      return this.replaceTalent({
        id: this.booking.id,
        payload: {
          talent: this.talent,
          replacement: this.replacement
        }
      }).then(() => {
        this.talent = null
        this.replacement = null

        this.$nextTick(() => {
          $(this.$el).modal('hide')
        })
      })
    },

    /**
     * The replacement options are scored differently as we always want to show the
     * "remove" option at the top of the list when it's available.
     *
     * @return int
     */
    replacementScoringCallback (option, selectizeScore) {
      if (option.id === 'remove') {
        return 1
      }

      return selectizeScore(option)
    },

    /**
     * Filter a talent array by the talent that are attending.
     *
     * This removes attending talent from the replacement talent.
     *
     * @return array
     */
    filterOutTalentThatAreAttending (talent) {
      return filter(talent, (talent) => {
        return indexOf(this.attendingTalentIds, talent.id) === -1
      })
    },

    /**
     * Map talent to the Selectize option format.
     *
     * @return object
     */
    mapTalentForSelectizeOption (talent) {
      return { id: talent.id, text: talent.name }
    }
  }
}
</script>
